import { randomInt } from '@/utils/number';
import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { BackgroundReplaceIcon } from '@/components/Icons';
import { useTranslation } from 'react-i18next';

import { getDocumentsProcessed } from './utils';

const COUNT_INITIAL = getDocumentsProcessed();
const MIN_RANDOM_NUM = 1;
const MAX_RANDOM_NUM = 7;
const MIN_RANDOM_SEC = 5;
const MAX_RANDOM_SEC = 10;

function EditedDocuments({ sx }) {
	const [count, setCount] = useState(COUNT_INITIAL);
	const [isFadeOutFinished, setIsFadeOutFinished] = useState(false);
	const [isRecentVisible, setIsRecentVisible] = useState(false);
	const [recent, setRecent] = useState(0);
	const refTimer = useRef(null);
	const { t } = useTranslation();

	useEffect(() => {
		let lastCount = count;

		function scheduleNext() {
			const delay = randomInt(MIN_RANDOM_SEC, MAX_RANDOM_SEC) * 1_000;

			function animateTotal(from, to) {
				const start = performance.now();
				const dur = 350;

				function step(now) {
					const progress = Math.min((now - start) / dur, 1);
					const eased = 1 - (1 - progress) ** 3;
					lastCount = Math.round(from + (to - from) * eased);

					setCount(lastCount);

					if (progress < 1) {
						requestAnimationFrame(step);
					} else {
						lastCount = to;
						setCount(to);
					}
				}

				requestAnimationFrame(step);
			}

			refTimer.current = setTimeout(() => {
				const added = randomInt(MIN_RANDOM_NUM, MAX_RANDOM_NUM);

				animateTotal(lastCount, lastCount + added);
				setRecent(added);
				setIsFadeOutFinished(false);
				setIsRecentVisible(true);

				setTimeout(() => {
					setIsRecentVisible(false);
				}, 1_500);

				setTimeout(() => {
					setIsFadeOutFinished(true);
				}, 2_000);

				scheduleNext();
			}, delay);
		}

		scheduleNext();

		return () => {
			if (refTimer.current) clearTimeout(refTimer.current);
		};
	}, []);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				borderRadius: '8px',
				border: 1,
				borderColor: 'divider',
				px: '16px',
				py: '12px',
				...sx
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '8px'
				}}
			>
				<Typography variant="body1" color="text.secondary">
					{t('fastCheckout.processedImages')}
				</Typography>

				<Box sx={{ position: 'relative' }}>
					<Typography component="span" variant="body1" fontWeight={500}>
						{count}
					</Typography>

					<Box
						sx={{
							position: 'absolute',
							top: 0,
							right: 0,
							transform: isFadeOutFinished
								? 'translateX(0)'
								: 'translateX(24px)',
							opacity: isRecentVisible ? 1 : 0,
							transition: 'opacity 500ms ease, transform 500ms ease',
							fontSize: 14,
							fontWeight: 600,
							color: 'primary.main',
							textShadow: '0 0 8px currentColor',
							pointerEvents: 'none'
						}}
					>
						+{recent}
					</Box>
				</Box>
			</Box>

			<Box
				component="span"
				sx={{
					display: 'flex',
					width: 32,
					height: 32,
					flexShrink: 0,
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius: '4px',
					bgcolor: 'neutral.150',
					color: 'primary.main'
				}}
			>
				<BackgroundReplaceIcon />
			</Box>
		</Box>
	);
}

export default EditedDocuments;
